const SEVERITIES = ["critical", "high", "medium", "low", "info"];
const isRecord = value => value !== null && typeof value === "object" && !Array.isArray(value);
const text = value => typeof value === "string" ? value.trim() : "";
const severity = value => text(value).toLowerCase();

function readNumber(value) {
    if (typeof value !== "number" && (typeof value !== "string" || !value.trim())) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function getMaintenance(report) {
    const wordpress = report.http?.wordpress_detected !== false && isRecord(report.wordpress)
        ? report.wordpress : {};
    const components = new Map();
    const addComponent = (component, type, fallback) => {
        if (!isRecord(component) || typeof component.outdated !== "boolean") return;
        const id = `${type}:${text(component.slug) || text(component.location) || fallback}`;
        const previous = components.get(id);
        // A theme may appear in both main_theme and themes; count it once.
        components.set(id, { type, outdated: component.outdated || previous?.outdated === true });
    };

    for (const [key, plugin] of Object.entries(wordpress.plugins || {})) addComponent(plugin, "plugin", key);
    for (const [key, theme] of Object.entries(wordpress.themes || {})) addComponent(theme, "theme", key);
    addComponent(wordpress.main_theme, "theme", "main_theme");
    // An insecure version is not necessarily outdated; require the explicit flag.
    addComponent(wordpress.version, "core", "wordpress");

    if (components.size > 0) {
        const outdated = [...components.values()].filter(component => component.outdated);
        return {
            kind: "components",
            value: outdated.length,
            assessed: components.size,
            plugins: outdated.filter(component => component.type === "plugin").length,
            themes: outdated.filter(component => component.type === "theme").length,
            core: outdated.filter(component => component.type === "core").length,
        };
    }

    const days = readNumber(report.tls?.days_until_expiry);
    if (days !== null) return { kind: "tls", value: days, version: text(report.tls?.tls_version) };
    return { kind: "unavailable", value: null };
}

export function getScanMetrics(report) {
    const findings = Array.isArray(report.findings)
        ? report.findings.filter(isRecord).map(finding => ({ ...finding, severity: severity(finding.severity) }))
        : [];
    const counts = Object.fromEntries(SEVERITIES.map(level => {
        const count = readNumber(report.summary?.counts?.[level]);
        return [level, count !== null && Number.isInteger(count) && count >= 0
            ? count : findings.filter(finding => finding.severity === level).length];
    }));

    let hostname = text(report.target?.hostname);
    if (!hostname) {
        try {
            hostname = new URL(text(report.target?.url) || text(report.http?.final_url) || text(report.http?.url)).hostname;
        } catch { /* The server name can still be displayed without a valid target URL. */ }
    }

    return {
        counts,
        findings,
        totalFindings: Object.values(counts).reduce((sum, count) => sum + count, 0),
        criticalHighCount: counts.critical + counts.high,
        priorityCount: counts.critical + counts.high + counts.medium,
        targetContext: [hostname, text(report.http?.server)].filter(Boolean).join(" · "),
        maintenance: getMaintenance(report),
    };
}
